import { useCallback, useRef, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Space, Tag, Modal, message, Select, Alert } from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { DataGrid, type DataGridHandle } from '@/components/DataGrid';
import type { SearchParams } from '@/api/types';
import {
  getEmailTemplates,
  deleteEmailTemplate,
  type EmailTemplate,
  type EmailTemplateFilters,
} from '@/api/adminApi';
import { useAuth } from '@/hooks/useAuth';
import AdminNav from './AdminNav';

const TEMPLATE_TYPES = ['welcome', 'reset_password', 'invoice', 'notification', 'ticket_update', 'campaign'];

export default function EmailTemplateListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const gridRef = useRef<DataGridHandle>(null);

  const [typeFilter, setTypeFilter] = useState<string | undefined>();
  const [activeFilter, setActiveFilter] = useState<boolean | undefined>();

  const canRead = hasPermission('admin', 'read');
  const canWrite = hasPermission('admin', 'write');
  const canDelete = hasPermission('admin', 'delete');

  const fetchData = useCallback(
    (params: SearchParams) => {
      const filters: EmailTemplateFilters = { ...params };
      if (typeFilter) filters.type = typeFilter;
      if (activeFilter !== undefined) filters.is_active = activeFilter;
      return getEmailTemplates(filters);
    },
    [typeFilter, activeFilter]
  );

  const handleDelete = useCallback(
    (id: string) => {
      Modal.confirm({
        title: t('app.confirm'),
        content: t('admin.confirm_delete_email_template'),
        okText: t('app.yes'),
        cancelText: t('app.no'),
        okType: 'danger',
        onOk: async () => {
          try {
            await deleteEmailTemplate(id);
            message.success(t('admin.email_template_delete_success'));
            gridRef.current?.reload();
          } catch {
            message.error(t('app.error'));
          }
        },
      });
    },
    [t]
  );

  const columns: ColumnsType<EmailTemplate> = useMemo(
    () => [
      {
        title: t('admin.email_template_field.name'),
        dataIndex: 'name',
        key: 'name',
        sorter: true,
        ellipsis: true,
      },
      {
        title: t('admin.email_template_field.type'),
        dataIndex: 'type',
        key: 'type',
        width: 140,
        render: (tp: string) => (
          <Tag>{t(`admin.email_template_type.${tp}`, tp)}</Tag>
        ),
      },
      {
        title: t('admin.email_template_field.subject'),
        dataIndex: 'subject',
        key: 'subject',
        ellipsis: true,
      },
      {
        title: t('admin.email_template_field.is_active'),
        dataIndex: 'is_active',
        key: 'is_active',
        width: 80,
        render: (v: boolean) => (
          <Tag color={v ? 'green' : 'default'}>
            {v ? t('app.yes') : t('app.no')}
          </Tag>
        ),
      },
      {
        title: t('admin.email_template_field.variables'),
        dataIndex: 'variables',
        key: 'variables',
        width: 200,
        render: (vars: string[]) =>
          vars && vars.length > 0
            ? vars.map((v) => <Tag key={v} style={{ marginBottom: 2 }}>{`{{${v}}}`}</Tag>)
            : '-',
      },
      {
        title: t('app.actions'),
        key: 'col_actions',
        width: 130,
        render: (_: unknown, record: EmailTemplate) => (
          <Space size="small">
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/admin/email-templates/${record.id}`)}
            />
            {canWrite && (
              <Button
                type="link"
                size="small"
                icon={<EditOutlined />}
                onClick={() => navigate(`/admin/email-templates/${record.id}/edit`)}
              />
            )}
            {canDelete && (
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(record.id)}
              />
            )}
          </Space>
        ),
      },
    ],
    [t, navigate, canWrite, canDelete, handleDelete]
  );

  const toolbar = (
    <Space>
      <Select
        allowClear
        placeholder={t('admin.email_template_field.type')}
        value={typeFilter}
        onChange={(v) => { setTypeFilter(v); gridRef.current?.reload(); }}
        style={{ width: 160 }}
        options={TEMPLATE_TYPES.map((tp) => ({
          value: tp,
          label: t(`admin.email_template_type.${tp}`),
        }))}
      />
      <Select
        allowClear
        placeholder={t('admin.email_template_field.is_active')}
        value={activeFilter}
        onChange={(v) => { setActiveFilter(v); gridRef.current?.reload(); }}
        style={{ width: 120 }}
        options={[
          { value: true, label: t('app.yes') },
          { value: false, label: t('app.no') },
        ]}
      />
      {canWrite && (
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/admin/email-templates/new')}
        >
          {t('admin.create_email_template_title')}
        </Button>
      )}
    </Space>
  );

  if (!canRead) return <Alert type="error" message={t('app.forbidden')} />;

  return (
    <div>
      <AdminNav />
      <h2>{t('admin.email_templates_title')}</h2>
      <DataGrid<EmailTemplate>
        ref={gridRef}
        columns={columns}
        fetchData={fetchData}
        rowKey="id"
        searchable
        searchPlaceholder={t('admin.search_email_templates_placeholder')}
        defaultSortField="created_at"
        defaultSortDescending
        toolbar={toolbar}
      />
    </div>
  );
}
